import { z } from 'zod';

// ---- enums ----
export const NotificationTypeSchema = z.enum([
  'SYSTEM_BROADCAST',
  'MOBILE_VERSION',
  'WEB_VERSION',
  'USER_PASSWORD_RESET',
  'USER_ENTITY_UPDATED',
  'PHOTO_COMMENT',
]);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

// ---- DTO ----
export const NotificationDtoSchema = z.object({
  id: z.string().uuid(),
  type: NotificationTypeSchema,
  titleKey: z.string().nullable(),
  bodyKey: z.string().nullable(),
  params: z.record(z.unknown()).nullable(),
  title: z.string(), // snapshot, always present
  body: z.string().nullable(),
  url: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  read: z.boolean(), // derived: readAt !== null
  readAt: z.string().nullable(),
  createdAt: z.string(),
});
export type NotificationDto = z.infer<typeof NotificationDtoSchema>;

// ---- list (cursor) ----
export const NotificationListQuerySchema = z.object({
  cursor: z.string().uuid().optional(), // last seen id; rows strictly older
  limit: z.coerce.number().int().min(1).max(50).default(20),
  filter: z.enum(['all', 'unread']).default('all'),
});
export type NotificationListQuery = z.infer<typeof NotificationListQuerySchema>;

export const NotificationListResponseSchema = z.object({
  items: z.array(NotificationDtoSchema),
  nextCursor: z.string().uuid().nullable(),
  unreadCount: z.number().int().nonnegative(),
});
export type NotificationListResponse = z.infer<typeof NotificationListResponseSchema>;

export const UnreadCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type UnreadCountResponse = z.infer<typeof UnreadCountResponseSchema>;

export const MarkAllReadResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
});
export type MarkAllReadResponse = z.infer<typeof MarkAllReadResponseSchema>;

// ---- push tokens (mobile / bearer) ----
export const PushTokenRegisterInputSchema = z
  .object({
    token: z.string().min(1).max(255), // ExponentPushToken[...]
    platform: z.enum(['ios', 'android']),
    deviceId: z.string().max(128).optional(),
  })
  .strict();
export type PushTokenRegisterInput = z.infer<typeof PushTokenRegisterInputSchema>;

export const PushTokenDtoSchema = z.object({
  id: z.string().uuid(),
  token: z.string(),
  platform: z.string(),
  deviceId: z.string().nullable(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
});
export type PushTokenDto = z.infer<typeof PushTokenDtoSchema>;

// ---- broadcast target ----
export const BroadcastTargetSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('all') }),
  z.object({
    mode: z.literal('roles'),
    roles: z
      .array(z.enum(['SUPER_ADMIN', 'CONTROLOR', 'ADMINISTRATOR', 'AJUTOR_ADMINISTRATOR']))
      .min(1),
  }),
  z.object({ mode: z.literal('entities'), entities: z.array(z.string().min(1)).min(1) }),
  z.object({ mode: z.literal('raioane'), raioane: z.array(z.string().min(1)).min(1) }),
  z.object({ mode: z.literal('users'), userIds: z.array(z.string().uuid()).min(1) }),
]);
export type BroadcastTarget = z.infer<typeof BroadcastTargetSchema>;

// ---- broadcast (SUPER_ADMIN) ----
export const BroadcastInputSchema = z
  .object({
    // Either a typed title or a titleKey; for admin free text we use `title`.
    title: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(2000),
    url: z.string().trim().max(500).optional(),
    target: BroadcastTargetSchema,
  })
  .strict();
export type BroadcastInput = z.infer<typeof BroadcastInputSchema>;

export const BroadcastResponseSchema = z.object({
  created: z.number().int().nonnegative(),
});
export type BroadcastResponse = z.infer<typeof BroadcastResponseSchema>;

export const PreviewRecipientsInputSchema = z
  .object({ target: BroadcastTargetSchema })
  .strict();
export type PreviewRecipientsInput = z.infer<typeof PreviewRecipientsInputSchema>;

export const PreviewRecipientsResponseSchema = z.object({
  count: z.number().int().nonnegative(),
  sample: z
    .array(
      z.object({
        id: z.string().uuid(),
        username: z.string(),
        role: z.string(),
      }),
    )
    .max(20),
});
export type PreviewRecipientsResponse = z.infer<typeof PreviewRecipientsResponseSchema>;

// ---- version announce (SUPER_ADMIN) ----
export const AnnounceVersionResponseSchema = z.object({
  announced: z.boolean(), // true if a fanout happened, false if no-op (watermark current)
  version: z.string(),
  created: z.number().int().nonnegative(),
});
export type AnnounceVersionResponse = z.infer<typeof AnnounceVersionResponseSchema>;
